import api from './api';

export const authService = {
    login: async (phoneNumber, password) => {
        const response = await api.post('/auth/login', { phoneNumber, password });
        return response.data;
    },

    registerSuperAdmin: async (data) => {
        const response = await api.post('/auth/register-superadmin', data);
        return response.data;
    }
};

export const superAdminService = {
    createDoctor: async (data) => {
        const response = await api.post('/superadmin/create-doctor', data);
        return response.data;
    },

    getDoctors: async () => {
        const response = await api.get('/superadmin/doctors');
        return response.data;
    },

    getDoctor: async (id) => {
        const response = await api.get(`/superadmin/doctor/${id}`);
        return response.data;
    },

    updateDoctor: async (id, data) => {
        const response = await api.put(`/superadmin/doctor/${id}`, data);
        return response.data;
    },

    deleteDoctor: async (id) => {
        const response = await api.delete(`/superadmin/doctor/${id}`);
        return response.data;
    },

    getDoctorReport: async (id) => {
        const response = await api.get(`/superadmin/reports/doctor/${id}`);
        return response.data;
    },

    getOverviewReport: async () => {
        const response = await api.get('/superadmin/reports/overview');
        return response.data;
    },

    renewDoctor: async (id) => {
        const response = await api.put(`/superadmin/doctor/${id}/renew`);
        return response.data;
    },

    // Enable/disable AI features for a doctor (off by default for everyone).
    setDoctorAiAccess: async (id, aiEnabled) => {
        const response = await api.put(`/superadmin/doctor/${id}/ai-access`, { aiEnabled });
        return response.data;
    },

    // Hospital admin management
    createHospitalAdmin: async (data) => {
        const response = await api.post('/superadmin/create-hospital-admin', data);
        return response.data;
    },

    getHospitalAdmins: async () => {
        const response = await api.get('/superadmin/hospital-admins');
        return response.data;
    },

    updateHospitalAdmin: async (id, data) => {
        const response = await api.put(`/superadmin/hospital-admin/${id}`, data);
        return response.data;
    },

    deleteHospitalAdmin: async (id) => {
        const response = await api.delete(`/superadmin/hospital-admin/${id}`);
        return response.data;
    }
};

export const doctorService = {
    createSecretary: async (data) => {
        const response = await api.post('/doctor/create-secretary', data);
        return response.data;
    },

    getSecretaries: async () => {
        const response = await api.get('/doctor/secretaries');
        return response.data;
    },

    updateSecretary: async (id, data) => {
        const response = await api.put(`/doctor/secretary/${id}`, data);
        return response.data;
    },

    deleteSecretary: async (id) => {
        const response = await api.delete(`/doctor/secretary/${id}`);
        return response.data;
    },

    // Pharmacy User Management
    createPharmacyUser: async (data) => {
        const response = await api.post('/doctor/create-pharmacy-user', data);
        return response.data;
    },

    getPharmacyUsers: async () => {
        const response = await api.get('/doctor/pharmacy-users');
        return response.data;
    },

    updatePharmacyUser: async (id, data) => {
        const response = await api.put(`/doctor/pharmacy-user/${id}`, data);
        return response.data;
    },

    deletePharmacyUser: async (id) => {
        const response = await api.delete(`/doctor/pharmacy-user/${id}`);
        return response.data;
    },

    // Cosmetic User Management
    createCosmeticUser: async (data) => {
        const response = await api.post('/doctor/create-cosmetic-user', data);
        return response.data;
    },

    getCosmeticUsers: async () => {
        const response = await api.get('/doctor/cosmetic-users');
        return response.data;
    },

    updateCosmeticUser: async (id, data) => {
        const response = await api.put(`/doctor/cosmetic-user/${id}`, data);
        return response.data;
    },

    deleteCosmeticUser: async (id) => {
        const response = await api.delete(`/doctor/cosmetic-user/${id}`);
        return response.data;
    },

    // Lab User Management
    createLabUser: async (data) => {
        const response = await api.post('/doctor/create-lab-user', data);
        return response.data;
    },

    getLabUsers: async () => {
        const response = await api.get('/doctor/lab-users');
        return response.data;
    },

    updateLabUser: async (id, data) => {
        const response = await api.put(`/doctor/lab-user/${id}`, data);
        return response.data;
    },

    deleteLabUser: async (id) => {
        const response = await api.delete(`/doctor/lab-user/${id}`);
        return response.data;
    },

    createDrug: async (data) => {
        const response = await api.post('/doctor/drugs', data);
        return response.data;
    },

    // What this doctor has already written in a complaint or diagnosis box, so
    // the visit form can offer it back instead of asking them to retype it.
    // Visits imported from the doctor's previous system, still waiting to be
    // matched to a patient.
    getLegacyRecords: async (params) => {
        const response = await api.get('/doctor/legacy-records', { params });
        return response.data;
    },

    attachLegacyRecord: async (id, patientId) => {
        const response = await api.put(`/doctor/legacy-records/${id}/attach`, { patientId });
        return response.data;
    },

    getVisitSuggestions: async () => {
        const response = await api.get('/doctor/visit-suggestions');
        return response.data;
    },

    getDrugs: async () => {
        const response = await api.get('/doctor/drugs');
        return response.data;
    },

    updateDrug: async (id, data) => {
        const response = await api.put(`/doctor/drugs/${id}`, data);
        return response.data;
    },

    deleteDrug: async (id) => {
        const response = await api.delete(`/doctor/drugs/${id}`);
        return response.data;
    },

    getPatients: async () => {
        const response = await api.get('/doctor/patients');
        return response.data;
    },

    getPatientHistory: async (id) => {
        const response = await api.get(`/doctor/patient/${id}/history`);
        return response.data;
    },

    getDashboard: async () => {
        const response = await api.get('/doctor/dashboard');
        return response.data;
    }
};

export const secretaryService = {
    getPatients: async () => {
        const response = await api.get('/secretary/patients');
        return response.data;
    },

    getDashboard: async () => {
        const response = await api.get('/secretary/dashboard');
        return response.data;
    }
};

export const patientService = {
    createPatient: async (data) => {
        const response = await api.post('/patient', data);
        return response.data;
    },

    // Does this person already have a file? Called while the form is being filled
    // in, so a returning patient is recognised before a second record is made.
    checkDuplicate: async ({ name, phoneNumber }) => {
        const response = await api.get('/patient/check-duplicate', {
            params: { name, phoneNumber }
        });
        return response.data;
    },

    getPatients: async () => {
        const response = await api.get('/patient');
        return response.data;
    },

    getPatient: async (id) => {
        const response = await api.get(`/patient/${id}`);
        return response.data;
    },

    updatePatient: async (id, data) => {
        const response = await api.put(`/patient/${id}`, data);
        return response.data;
    },

    deletePatient: async (id) => {
        const response = await api.delete(`/patient/${id}`);
        return response.data;
    },

    createVisit: async (patientId, data) => {
        const response = await api.post(`/patient/${patientId}/visit`, data);
        return response.data;
    },

    getVisits: async (patientId) => {
        const response = await api.get(`/patient/${patientId}/visits`);
        return response.data;
    },

    updateVisit: async (visitId, data) => {
        const response = await api.put(`/patient/visit/${visitId}`, data);
        return response.data;
    },

    // Removes the visit from the patient's history. The record is kept on the
    // server rather than destroyed, so a mistaken delete can be undone there.
    deleteVisit: async (visitId) => {
        const response = await api.delete(`/patient/visit/${visitId}`);
        return response.data;
    },

    // Branch doctor: send a visit's prescriptions to the branch pharmacy queue.
    sendPrescriptionToPharmacy: async (visitId) => {
        const response = await api.post(`/patient/visit/${visitId}/send-prescription`);
        return response.data;
    }
};

export const uploadService = {
    uploadAttachment: async (visitId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/upload/attachment/${visitId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    deleteAttachment: async (visitId, attachmentId) => {
        const response = await api.delete(`/upload/attachment/${visitId}/${attachmentId}`);
        return response.data;
    },

    // An X-ray or CT film, filed against the modality it belongs to rather than
    // the visit's general attachments. `modality` is 'xray' or 'ctScan'.
    uploadImaging: async (visitId, modality, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/upload/imaging/${visitId}/${modality}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    deleteImaging: async (visitId, modality, fileId) => {
        const response = await api.delete(`/upload/imaging/${visitId}/${modality}/${fileId}`);
        return response.data;
    }
};

export const reportService = {
    getPatientReport: async (id) => {
        const response = await api.get(`/report/patient/${id}`);
        return response.data;
    }
};

export { default as settingsService } from './settingsService';
export { default as appointmentService } from './appointmentService';
export { default as reklamSliderService } from './reklamSliderService';
export { default as pharmacyService } from './pharmacyService';
export { default as cosmeticService } from './cosmeticService';
export { default as labService } from './labService';
export { default as hospitalAdminService } from './hospitalAdminService';
export { default as dataService } from './dataService';
export { default as glassesOrderService } from './glassesOrderService';
export { default as dentalService } from './dentalService';